// src/pages/monitoring/issues/IssuesList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye, AlertTriangle, AlertCircle, CheckCircle,
  TrendingUp, Activity, Building2, Network, User,
  Calendar, X, FileText, Edit, RotateCcw, XCircle,
  ChevronDown, ChevronUp, Download, Plus
} from 'lucide-react';
import toast from 'react-hot-toast';
import DataTable from '../../../components/common/DataTable';
import issueService, {
  getStatusBadgeClass,
  getPriorityBadgeClass,
  getStatusLabel,
  getPriorityLabel,
} from '../../../services/issueService';

// ── Detail Modal ─────────────────────────────────────────────────────

const IssueDetailModal = ({ issue, onClose, onAssign, onResolve, onClose2, onReopen, onCancel }) => {
  if (!issue) return null;

  const isActive = issue.isActive;
  const isOverdue = issue.isOverdue;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-t-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-bold">Issue Details — {issue.issueId}</h3>
              <p className="text-red-100 text-sm mt-1">{issue.title}</p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Overdue warning */}
          {isOverdue && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm font-medium">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              Issue ini sudah melewati due date dan belum diselesaikan.
            </div>
          )}

          {/* Project Info */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Project Information
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Project Code:</span>
                <p className="font-semibold text-gray-900">{issue.projectCode}</p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Project Name:</span>
                <p className="font-semibold text-gray-900">{issue.projectName}</p>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Type:</span>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold mt-1 ${
                  issue.projectType === 'TOWER' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                }`}>
                  {issue.projectType === 'TOWER' ? <Building2 className="w-3 h-3" /> : <Network className="w-3 h-3" />}
                  {issue.projectType}
                </span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Target:</span>
                <p className="font-semibold text-gray-900">
                  {issue.foSegmentName || issue.towerSiteName || '—'}
                </p>
              </div>
              {issue.milestoneName && (
                <div>
                  <span className="text-blue-700 font-medium">Milestone:</span>
                  <p className="font-semibold text-gray-900">{issue.milestoneName}</p>
                </div>
              )}
              <div>
                <span className="text-blue-700 font-medium">Scope:</span>
                <p className="font-semibold text-gray-900">{issue.issueScopeDisplay}</p>
              </div>
            </div>
          </div>

          {/* Issue Meta */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <label className="block font-bold text-gray-700 mb-1">Category</label>
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                {issue.categoryIcon} {issue.categoryDisplay}
              </span>
            </div>
            <div>
              <label className="block font-bold text-gray-700 mb-1">Priority</label>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold border ${getPriorityBadgeClass(issue.priority)}`}>
                {issue.priorityDisplay}
              </span>
            </div>
            <div>
              <label className="block font-bold text-gray-700 mb-1">Status</label>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold border ${getStatusBadgeClass(issue.status)}`}>
                {getStatusLabel(issue.status)}
              </span>
            </div>
            <div>
              <label className="block font-bold text-gray-700 mb-1">Due Date</label>
              <p className="flex items-center gap-2 text-gray-900">
                <Calendar className="w-4 h-4 text-gray-400" />
                {issue.dueDate ?? <span className="text-gray-400 italic">Belum diset</span>}
              </p>
            </div>
            <div>
              <label className="block font-bold text-gray-700 mb-1">Reported By</label>
              <p className="flex items-center gap-2 text-gray-900">
                <User className="w-4 h-4 text-gray-400" /> {issue.createdByFullName}
              </p>
            </div>
            <div>
              <label className="block font-bold text-gray-700 mb-1">Assigned To</label>
              <p className="flex items-center gap-2 text-gray-900">
                <User className="w-4 h-4 text-gray-400" />
                {issue.assignedToFullName ?? <span className="text-gray-400 italic">Belum di-assign</span>}
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
            <p className="text-gray-900 bg-gray-50 p-4 rounded-lg border-2 border-gray-200 text-sm leading-relaxed">
              {issue.description}
            </p>
          </div>

          {/* Photos */}
          {issue.photos?.length > 0 && (
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Photo Evidence ({issue.photos.length})
              </label>
              <div className="flex gap-3 flex-wrap">
                {issue.photos.map((photo) => (
                  <a key={photo.id} href={photo.url} target="_blank" rel="noopener noreferrer"
                    className="w-20 h-20 rounded-lg border-2 border-gray-200 overflow-hidden hover:border-red-400 transition-colors flex items-center justify-center bg-gray-50">
                    <img src={photo.url} alt={photo.originalFilename}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Resolution */}
          {issue.resolution && (
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <label className="block text-sm font-bold text-green-800 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Resolution
              </label>
              <p className="text-green-900 text-sm">{issue.resolution}</p>
              {issue.resolvedAt && (
                <p className="text-xs text-green-700 mt-2 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Resolved by {issue.resolvedByFullName} on {new Date(issue.resolvedAt).toLocaleString('id-ID')}
                </p>
              )}
            </div>
          )}

          {/* Cancel reason */}
          {issue.cancelReason && (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">Cancel Reason</label>
              <p className="text-gray-700 text-sm">{issue.cancelReason}</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end gap-3 flex-wrap">
          <button onClick={onClose}
            className="px-5 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors">
            Tutup
          </button>
          {issue.status === 'OPEN' || issue.status === 'REOPENED' ? (
            <button onClick={() => onAssign(issue)}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-md transition-all flex items-center gap-2">
              <User className="w-4 h-4" /> Assign & Handle
            </button>
          ) : null}
          {issue.status === 'IN_PROGRESS' ? (
            <button onClick={() => onResolve(issue)}
              className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium shadow-md transition-all flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Mark Resolved
            </button>
          ) : null}
          {issue.status === 'RESOLVED' ? (
            <>
              <button onClick={() => onReopen(issue)}
                className="px-5 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium shadow-md transition-all flex items-center gap-2">
                <RotateCcw className="w-4 h-4" /> Reopen
              </button>
              <button onClick={() => onClose2(issue)}
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium shadow-md transition-all flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Close Issue
              </button>
            </>
          ) : null}
          {isActive && issue.status !== 'CANCELLED' ? (
            <button onClick={() => onCancel(issue)}
              className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-md transition-all flex items-center gap-2">
              <XCircle className="w-4 h-4" /> Cancel
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ── Assign Modal ──────────────────────────────────────────────────────

const AssignModal = ({ issue, onClose, onConfirm }) => {
  const [assignedToUserId, setAssignedToUserId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignmentNote, setAssignmentNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!assignedToUserId) {
      toast.error('Pilih user yang akan di-assign');
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm({ assignedToUserId: Number(assignedToUserId), dueDate, assignmentNote });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-lg">
          <h3 className="font-bold text-lg">Assign Issue — {issue?.issueId}</h3>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Assign To <span className="text-red-500">*</span>
            </label>
            {/* In production: fetch user list from API. Using text input for now. */}
            <input type="number" placeholder="User ID"
              value={assignedToUserId} onChange={(e) => setAssignedToUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
            <p className="text-xs text-gray-500 mt-1">
              Note: Integrate dengan user selector dari API /users
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Note (Optional)</label>
            <textarea rows={3} value={assignmentNote} onChange={(e) => setAssignmentNote(e.target.value)}
              placeholder="Instruksi atau catatan untuk tim..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium text-sm">
            Batal
          </button>
          <button onClick={handleSubmit} disabled={isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm disabled:opacity-60">
            {isSubmitting ? 'Saving...' : 'Assign & Handle'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Resolve Modal ─────────────────────────────────────────────────────

const ResolveModal = ({ issue, onClose, onConfirm }) => {
  const [resolution, setResolution] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!resolution.trim()) {
      toast.error('Resolution notes wajib diisi');
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm({ resolution });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-t-lg">
          <h3 className="font-bold text-lg">Resolve Issue — {issue?.issueId}</h3>
        </div>
        <div className="p-5">
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Resolution Notes <span className="text-red-500">*</span>
          </label>
          <textarea rows={5} value={resolution} onChange={(e) => setResolution(e.target.value)}
            placeholder="Jelaskan tindakan yang telah diambil untuk menyelesaikan issue ini..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm resize-none" />
        </div>
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium text-sm">
            Batal
          </button>
          <button onClick={handleSubmit} disabled={isSubmitting}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-60">
            {isSubmitting ? 'Saving...' : 'Mark Resolved'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Reopen / Cancel Modal (shared) ────────────────────────────────────

const ReasonModal = ({ title, label, placeholder, confirmLabel, confirmColor, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error(`${label} wajib diisi`);
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm(reason);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div className={`p-5 border-b border-gray-200 ${confirmColor} text-white rounded-t-lg`}>
          <h3 className="font-bold text-lg">{title}</h3>
        </div>
        <div className="p-5">
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {label} <span className="text-red-500">*</span>
          </label>
          <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 text-sm resize-none" />
        </div>
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium text-sm">
            Batal
          </button>
          <button onClick={handleSubmit} disabled={isSubmitting}
            className={`px-4 py-2 text-white rounded-lg font-medium text-sm disabled:opacity-60 ${confirmColor} hover:opacity-90`}>
            {isSubmitting ? 'Saving...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────

const IssuesList = () => {
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────
  const [issues, setIssues]           = useState([]);
  const [stats, setStats]             = useState(null);
  const [isLoading, setIsLoading]     = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize]       = useState(10);
  const [sortConfig, setSortConfig]   = useState({ sortBy: 'createdAt', direction: 'DESC' });

  // Filters
  const [keyword, setKeyword]               = useState('');
  const [filterProjectType, setFilterProjectType] = useState('');
  const [filterStatus, setFilterStatus]     = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showFilters, setShowFilters]       = useState(true);

  // Modals
  const [selectedIssue, setSelectedIssue]   = useState(null);
  const [activeModal, setActiveModal]       = useState(null);
  // activeModal: 'detail' | 'assign' | 'resolve' | 'reopen' | 'cancel'

  // ── Fetch ──────────────────────────────────────────────────────────

  const fetchIssues = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page:        currentPage,
        size:        pageSize,
        keyword:     keyword || undefined,
        projectType: filterProjectType || undefined,
        status:      filterStatus      || undefined,
        priority:    filterPriority    || undefined,
        category:    filterCategory    || undefined,
      };
      const data = await issueService.getIssues(params);
      // Handle Spring Page or plain array defensively
      if (Array.isArray(data)) {
        setIssues(data);
        setTotalElements(data.length);
      } else {
        setIssues(data.content ?? []);
        setTotalElements(data.totalElements ?? 0);
      }
    } catch (err) {
      console.error('Error fetching issues:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, keyword, filterProjectType, filterStatus, filterPriority, filterCategory]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await issueService.getIssueStatistics();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => { fetchIssues(); }, [fetchIssues]);
  useEffect(() => { fetchStats();  }, [fetchStats]);

  // ── Action Handlers ────────────────────────────────────────────────

  const openDetail = async (row) => {
    try {
      const detail = await issueService.getIssueById(row.id);
      setSelectedIssue(detail);
      setActiveModal('detail');
    } catch (err) {
      toast.error('Gagal memuat detail issue');
    }
  };

  const closeModal = () => { setSelectedIssue(null); setActiveModal(null); };

  const handleAssignConfirm = async (data) => {
    try {
      await issueService.assignIssue(selectedIssue.id, data);
      toast.success('Issue berhasil di-assign ke penanganan');
      closeModal();
      fetchIssues();
      fetchStats();
    } catch (err) {
      toast.error('Gagal assign issue');
    }
  };

  const handleResolveConfirm = async (data) => {
    try {
      await issueService.resolveIssue(selectedIssue.id, data);
      toast.success('Issue berhasil di-resolved');
      closeModal();
      fetchIssues();
      fetchStats();
    } catch (err) {
      toast.error('Gagal resolve issue');
    }
  };

  const handleCloseIssue = async (issue) => {
    if (!window.confirm(`Tutup issue ${issue.issueId}? Tindakan ini mengkonfirmasi bahwa issue sudah selesai.`)) return;
    try {
      await issueService.closeIssue(issue.id);
      toast.success('Issue berhasil di-closed');
      closeModal();
      fetchIssues();
      fetchStats();
    } catch (err) {
      toast.error('Gagal close issue');
    }
  };

  const handleReopenConfirm = async (reason) => {
    try {
      await issueService.reopenIssue(selectedIssue.id, { reopenReason: reason });
      toast.success('Issue berhasil di-reopen');
      closeModal();
      fetchIssues();
      fetchStats();
    } catch (err) {
      toast.error('Gagal reopen issue');
    }
  };

  const handleCancelConfirm = async (reason) => {
    try {
      await issueService.cancelIssue(selectedIssue.id, { cancelReason: reason });
      toast.success('Issue berhasil di-cancel');
      closeModal();
      fetchIssues();
      fetchStats();
    } catch (err) {
      toast.error('Gagal cancel issue');
    }
  };

  const handleClearFilters = () => {
    setKeyword('');
    setFilterProjectType('');
    setFilterStatus('');
    setFilterPriority('');
    setFilterCategory('');
    setCurrentPage(0);
  };

  const hasActiveFilters = keyword || filterProjectType || filterStatus || filterPriority || filterCategory;

  // ── Table Columns ──────────────────────────────────────────────────

  const columns = [
    {
      key: 'no', label: 'No', sortable: false, width: 'w-14',
      render: (row, index) => (
        <span className="font-medium text-gray-700">{currentPage * pageSize + index + 1}</span>
      ),
    },
    {
      key: 'issueId', label: 'Issue ID', sortable: true,
      render: (row) => (
        <span className="font-bold text-blue-600 cursor-pointer hover:underline"
          onClick={() => openDetail(row)}>
          {row.issueId}
        </span>
      ),
    },
    {
      key: 'title', label: 'Title', sortable: true, nowrap: false,
      render: (row) => (
        <div>
          <div className="font-semibold text-gray-900 text-sm">{row.title}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            {row.categoryIcon} {row.categoryDisplay}
          </div>
        </div>
      ),
    },
    {
      key: 'projectCode', label: 'Project', sortable: true,
      render: (row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.projectCode}</div>
          <div className="text-xs text-gray-500 truncate max-w-[160px]">{row.projectName}</div>
        </div>
      ),
    },
    {
      key: 'projectType', label: 'Type', sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
          row.projectType === 'TOWER' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
        }`}>
          {row.projectType === 'TOWER' ? <Building2 className="w-3 h-3" /> : <Network className="w-3 h-3" />}
          {row.projectType}
        </span>
      ),
    },
    {
      key: 'targetName', label: 'Target', sortable: false,
      render: (row) => (
        <div>
          <div className="text-sm text-gray-900">{row.targetName ?? <span className="text-gray-400 italic">Project Level</span>}</div>
          {row.milestoneName && <div className="text-xs text-gray-500">{row.milestoneName}</div>}
        </div>
      ),
    },
    {
      key: 'priority', label: 'Priority', sortable: true,
      render: (row) => (
        <span className={`px-2 py-1 text-xs font-bold rounded-full border ${getPriorityBadgeClass(row.priority)}`}>
          {row.priorityDisplay ?? row.priority}
        </span>
      ),
    },
    {
      key: 'status', label: 'Status', sortable: true,
      render: (row) => (
        <div>
          <span className={`px-2 py-1 text-xs font-bold rounded-full border ${getStatusBadgeClass(row.status)}`}>
            {getStatusLabel(row.status)}
          </span>
          {row.isOverdue && (
            <div className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Overdue
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'assignedToFullName', label: 'Assignee', sortable: false,
      render: (row) => (
        <div className="flex items-center gap-1 text-sm text-gray-700">
          <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
          {row.assignedToFullName ?? <span className="text-gray-400 italic text-xs">Unassigned</span>}
        </div>
      ),
    },
    {
      key: 'dueDate', label: 'Due Date', sortable: true,
      render: (row) => (
        <div className={`flex items-center gap-1 text-sm ${row.isOverdue ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
          <Calendar className="w-3 h-3 flex-shrink-0" />
          {row.dueDate ?? <span className="text-gray-400 italic text-xs">—</span>}
        </div>
      ),
    },
    {
      key: 'actions', label: 'Actions', sortable: false,
      render: (row) => (
        <button onClick={() => openDetail(row)}
          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
          title="View Detail">
          <Eye className="w-5 h-5" />
        </button>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center shadow-md">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Issues Tracking</h1>
              <p className="text-gray-600 mt-1">Monitor dan kelola issues di seluruh project</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/monitoring/issues/create')}
            className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all">
            <Plus className="w-5 h-5" />
            Buat Issue
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Critical + High */}
          <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg shadow-lg p-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <span className="text-xs font-semibold px-2 py-1 bg-white bg-opacity-20 rounded-full">CRITICAL</span>
            </div>
            <div className="text-3xl font-bold">{stats.totalCritical}</div>
            <div className="text-purple-100 text-sm mt-1">Critical Open</div>
            <div className="text-xs text-purple-200 mt-2">{stats.criticalOpen} active</div>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <span className="text-xs font-semibold px-2 py-1 bg-white bg-opacity-20 rounded-full">HIGH</span>
            </div>
            <div className="text-3xl font-bold">{stats.totalHigh}</div>
            <div className="text-red-100 text-sm mt-1">High Priority</div>
            <div className="text-xs text-red-200 mt-2">{stats.highOpen} active</div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5" />
              </div>
              <span className="text-xs font-semibold px-2 py-1 bg-white bg-opacity-20 rounded-full">MEDIUM</span>
            </div>
            <div className="text-3xl font-bold">{stats.totalMedium}</div>
            <div className="text-yellow-100 text-sm mt-1">Medium Priority</div>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-5 text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5" />
              </div>
              <span className="text-xs font-semibold px-2 py-1 bg-white bg-opacity-20 rounded-full">STATUS</span>
            </div>
            <div className="text-3xl font-bold">
              {(stats.totalOpen ?? 0) + (stats.totalInProgress ?? 0) + (stats.totalReopened ?? 0)}
            </div>
            <div className="text-blue-100 text-sm mt-1">Total Active</div>
            <div className="grid grid-cols-3 gap-1 text-xs mt-2 pt-2 border-t border-white border-opacity-20">
              <div className="text-center"><div className="font-bold">{stats.totalOpen}</div><div className="text-blue-200">Open</div></div>
              <div className="text-center"><div className="font-bold">{stats.totalInProgress}</div><div className="text-blue-200">Progress</div></div>
              <div className="text-center"><div className="font-bold">{stats.totalResolved}</div><div className="text-blue-200">Resolved</div></div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-4 border-b border-gray-100">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <input type="text" value={keyword} onChange={(e) => { setKeyword(e.target.value); setCurrentPage(0); }}
                placeholder="Search Issue ID, judul, atau deskripsi..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm" />
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <button onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 border-2 rounded-lg font-medium flex items-center gap-2 text-sm transition-all ${
                showFilters || hasActiveFilters
                  ? 'border-red-600 bg-red-50 text-red-600'
                  : 'border-gray-300 text-gray-700 hover:border-red-500'
              }`}>
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Filter
              {hasActiveFilters && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-600 text-white rounded-full text-xs font-bold">Active</span>
              )}
            </button>

            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2 text-sm shadow-md">
              <Download className="w-4 h-4" /> Export
            </button>
          </div>

          {/* Filter panel */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Project Type</label>
                  <select value={filterProjectType}
                    onChange={(e) => { setFilterProjectType(e.target.value); setCurrentPage(0); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500">
                    <option value="">All Types</option>
                    <option value="TOWER">TOWER</option>
                    <option value="FO">FO</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
                  <select value={filterStatus}
                    onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(0); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500">
                    <option value="">All Status</option>
                    <option value="OPEN">Open</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="CLOSED">Closed</option>
                    <option value="REOPENED">Reopened</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Priority</label>
                  <select value={filterPriority}
                    onChange={(e) => { setFilterPriority(e.target.value); setCurrentPage(0); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500">
                    <option value="">All Priorities</option>
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Category</label>
                  <select value={filterCategory}
                    onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(0); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500">
                    <option value="">All Categories</option>
                    <option value="MATERIAL">🏗️ Material</option>
                    <option value="CUACA">🌧️ Cuaca</option>
                    <option value="AKSES">🚧 Akses</option>
                    <option value="TEKNIS">⚙️ Teknis</option>
                    <option value="TENAGA_KERJA">👷 Tenaga Kerja</option>
                    <option value="KESELAMATAN">🛡️ Keselamatan (K3)</option>
                    <option value="COMMUNITY_CASE">🤝 Community Case</option>
                    <option value="PERIZINAN">📋 Perizinan</option>
                    <option value="VENDOR_SUBKON">🏢 Vendor & Subkon</option>
                    <option value="ANGGARAN">💰 Anggaran</option>
                    <option value="JADWAL">📅 Jadwal</option>
                    <option value="CUSTOMER_KONTRAK">🤝 Customer & Kontrak</option>
                    <option value="DOKUMEN_ADMINISTRASI">📄 Dokumen & Admin</option>
                    <option value="LAINNYA">🔧 Lainnya</option>
                  </select>
                </div>
                <div className="flex items-end">
                  {hasActiveFilters && (
                    <button onClick={handleClearFilters}
                      className="w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg font-medium border border-red-300 transition-colors">
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={issues}
          currentPage={currentPage}
          pageSize={pageSize}
          totalElements={totalElements}
          onPageChange={setCurrentPage}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(0); }}
          onSort={setSortConfig}
          sortConfig={sortConfig}
          isLoading={isLoading}
          searchValue={keyword}
          hoverColor="hover:bg-red-50"
        />

        {/* Footer Summary */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-between items-center flex-wrap gap-3">
          <div className="text-sm text-gray-700">
            Menampilkan <span className="font-bold text-red-600">{issues.length}</span> dari{' '}
            <span className="font-bold">{totalElements}</span> issues
          </div>
          <div className="flex gap-3 text-xs">
            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded font-bold">CRITICAL: {stats?.totalCritical ?? '—'}</span>
            <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-bold">HIGH: {stats?.totalHigh ?? '—'}</span>
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded font-bold">MEDIUM: {stats?.totalMedium ?? '—'}</span>
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-bold">LOW: {stats?.totalLow ?? '—'}</span>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {activeModal === 'detail' && selectedIssue && (
        <IssueDetailModal
          issue={selectedIssue}
          onClose={closeModal}
          onAssign={() => setActiveModal('assign')}
          onResolve={() => setActiveModal('resolve')}
          onClose2={handleCloseIssue}
          onReopen={() => setActiveModal('reopen')}
          onCancel={() => setActiveModal('cancel')}
        />
      )}

      {/* Assign Modal */}
      {activeModal === 'assign' && selectedIssue && (
        <AssignModal issue={selectedIssue} onClose={closeModal} onConfirm={handleAssignConfirm} />
      )}

      {/* Resolve Modal */}
      {activeModal === 'resolve' && selectedIssue && (
        <ResolveModal issue={selectedIssue} onClose={closeModal} onConfirm={handleResolveConfirm} />
      )}

      {/* Reopen Modal */}
      {activeModal === 'reopen' && selectedIssue && (
        <ReasonModal
          title={`Reopen Issue — ${selectedIssue.issueId}`}
          label="Alasan Reopen"
          placeholder="Jelaskan mengapa issue perlu dibuka kembali (resolusi belum memadai)..."
          confirmLabel="Reopen Issue"
          confirmColor="bg-orange-500"
          onClose={closeModal}
          onConfirm={handleReopenConfirm}
        />
      )}

      {/* Cancel Modal */}
      {activeModal === 'cancel' && selectedIssue && (
        <ReasonModal
          title={`Cancel Issue — ${selectedIssue.issueId}`}
          label="Alasan Cancel"
          placeholder="Jelaskan alasan pembatalan (misal: duplikat, issue tidak valid)..."
          confirmLabel="Cancel Issue"
          confirmColor="bg-red-600"
          onClose={closeModal}
          onConfirm={handleCancelConfirm}
        />
      )}
    </div>
  );
};

export default IssuesList;